'use client'

import { createContext, useCallback, useContext, useState } from 'react'
import type { ARTICLE_STATE } from './types'

type ArticleStateContextValue = {
  states: Record<string, ARTICLE_STATE>
  collectionSlug: string
  updateArticleState: (articleId: string, readingStatus: ARTICLE_STATE | null) => void
}

const ArticleStateContext = createContext<ArticleStateContextValue | null>(null)

type ArticleStateProviderProps = {
  collectionSlug: string
  initialStates: Record<string, ARTICLE_STATE>
  children: React.ReactNode
}

export function ArticleStateProvider({
  collectionSlug,
  initialStates,
  children
}: ArticleStateProviderProps) {
  const [states, setStates] = useState(initialStates)

  const updateArticleState = useCallback(
    (articleId: string, readingStatus: ARTICLE_STATE | null) => {
      setStates(prev => {
        if (readingStatus === null) {
          const next = { ...prev }
          delete next[articleId]
          return next
        }

        return { ...prev, [articleId]: readingStatus }
      })
    },
    []
  )

  return (
    <ArticleStateContext.Provider value={{ states, collectionSlug, updateArticleState }}>
      {children}
    </ArticleStateContext.Provider>
  )
}

export function useArticleStatesContext() {
  const ctx = useContext(ArticleStateContext)
  if (!ctx) throw new Error('useArticleStatesContext must be inside ArticleStateProvider')
  return ctx
}
